// src/pages/SignUp.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Divider,
  Checkbox,
  FormControlLabel,
  Alert,
  Snackbar,
  Link as MUILink,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useNavigate, useLocation, Link } from "react-router-dom";
import EmailIcon from "@mui/icons-material/Email";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import StadiumIcon from "@mui/icons-material/Stadium";
import HeadphonesIcon from "@mui/icons-material/Headphones";
import VideocamIcon from "@mui/icons-material/Videocam";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import PersonIcon from "@mui/icons-material/Person";
import PlaceIcon from "@mui/icons-material/Place";
import { authService } from "../services/resources.service";
import CitySelect from "../components/CitySelect";
import { getMessage, useLocale } from "../i18n/locale";
import { buildLegalConsent, LEGAL_DOCUMENTS } from "../legal/documents";

async function signUpWithEmail(payload) {
  return authService.register(payload);
}

// ---- Helpers ----
function passwordScore(pw) {
  // 0..4 score
  let score = 0;
  if (pw.length >= 8) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[a-z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
}
function strengthLabel(score) {
  return ["Very weak", "Weak", "Okay", "Good", "Strong"][score] || "Weak";
}
function roleLabel(role) {
  return role === "VENUE" ? "Venue name" : role === "STUDIO" ? "Studio name" : role === "MEDIA" ? "Media name" : "Artist/Band name";
}

export default function SignUp({ onAuth }) {
  const theme = useTheme();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const location = useLocation();
  const redirectTo = new URLSearchParams(location.search).get("redirect");

  // Form state
  const [role, setRole] = useState("GUEST");
  const [fullName, setFullName] = useState("");
  const [orgName, setOrgName] = useState("");
  const [city, setCity] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [agree, setAgree] = useState(false);

  const [companyWebsite, setCompanyWebsite] = useState("");
  const [formToken, setFormToken] = useState("");
  const [tokenGeneration, setTokenGeneration] = useState(0);
  const [protectionError, setProtectionError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let timer;
    let refresh;
    setFormToken("");
    const prepare = async () => {
      try {
        const result = await authService.signupToken();
        if (cancelled) return;
        setProtectionError(false);
        timer = setTimeout(() => setFormToken(result.token), result.readyAfterSeconds * 1000);
        refresh = setTimeout(() => setTokenGeneration((value) => value + 1),
          Math.max(result.readyAfterSeconds + 1, result.expiresAfterSeconds - 30) * 1000);
      } catch {
        if (cancelled) return;
        setProtectionError(true);
        timer = setTimeout(prepare, 10000);
      }
    };
    prepare();
    return () => { cancelled = true; clearTimeout(timer); clearTimeout(refresh); };
  }, [tokenGeneration]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "info" });

  const pwScore = useMemo(() => passwordScore(password), [password]);
  const passwordsMatch = confirm.length === 0 || password === confirm;

  const canSubmit = useMemo(() => {
    if (!agree) return false;
    if (!email || !fullName.trim()) return false;
    if (role !== "GUEST" && (!orgName || !city)) return false;
    if (password.length < 8 || !passwordsMatch) return false;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
    return true;
  }, [agree, city, email, fullName, orgName, password, passwordsMatch, role]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit || loading || !formToken) return;
    setLoading(true);
    setError("");
    try {
      const res = await signUpWithEmail({
        role,
        displayName: fullName,
        fullName,
        orgName,
        city,
        email,
        password,        legalConsent: buildLegalConsent(locale),
        formToken,
        companyWebsite,
      });
      setSnack({ open: true, msg: res.message, severity: "success" });
      navigate("/auth", {
        replace: true,
        state: { activationPending: true, email: res.email },
      });
    } catch (err) {
      setError(err.code === "SIGNUP_RETRY" ? "SIGNUP_RETRY" : err.message || "SIGNUP_RETRY");
      if (err.code === "SIGNUP_RETRY") setTokenGeneration((value) => value + 1);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", p: 2 }}>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 520,
          p: 2,
          borderRadius: 3,
          border: 1,
          borderColor: "divider",
        }}
      >
        <Stack spacing={2}>
          {/* Brand / Title */}
          <Stack spacing={0.5} alignItems="center" sx={{ textAlign: "center" }}>
            <Typography variant="h5" fontWeight={900}>
              Create your Band-Joe account
            </Typography>
            <Typography variant="body2" color="text.secondary">
              One account to manage your pages, gigs, and messages.
            </Typography>
          </Stack>

          {/* Role selector */}
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">
              I am signing up as
            </Typography>
            <ToggleButtonGroup
              value={role}
              exclusive
              onChange={(_, v) => v && setRole(v)}
              fullWidth
              size="small"
              sx={{
                flexWrap: "wrap",
                "& .MuiToggleButton-root": { borderRadius: 999, flex: "1 1 120px" },
              }}
            >
              <ToggleButton value="GUEST">
                <FavoriteBorderIcon sx={{ mr: 0.5 }} /> {getMessage(locale, "accountRoleGUEST")}
              </ToggleButton>
              <ToggleButton value="ARTIST">
                <MusicNoteIcon sx={{ mr: 0.5 }} /> {getMessage(locale, "accountRoleARTIST")}
              </ToggleButton>
              <ToggleButton value="VENUE">
                <StadiumIcon sx={{ mr: 0.5 }} /> {getMessage(locale, "accountRoleVENUE")}
              </ToggleButton>
              <ToggleButton value="STUDIO">
                <HeadphonesIcon sx={{ mr: 0.5 }} /> {getMessage(locale, "accountRoleSTUDIO")}
              </ToggleButton>
              <ToggleButton value="MEDIA">
                <VideocamIcon sx={{ mr: 0.5 }} /> {getMessage(locale, "accountRoleMEDIA")}
              </ToggleButton>
            </ToggleButtonGroup>
            {role === "GUEST" && (
              <Typography variant="body2" color="text.secondary">
                {getMessage(locale, "signupGuestDescription")}
              </Typography>
            )}
          </Stack>

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <Box aria-hidden="true" sx={{ position: "absolute", inlineSize: 1, blockSize: 1, overflow: "hidden", clipPath: "inset(50%)" }}>
              <input name="companyWebsite" aria-label={getMessage(locale, "signupWebsite")}
                tabIndex={-1} autoComplete="off" value={companyWebsite}
                onChange={(event) => setCompanyWebsite(event.target.value)} />
            </Box>
            <Stack spacing={1.25}>
              <TextField
                label="Your name"
                placeholder="e.g., Thando Mokoena"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon />
                    </InputAdornment>
                  ),
                }}
              />

              {role !== "GUEST" && <TextField
                label={roleLabel(role)}
                placeholder={
                  role === "VENUE"
                    ? "e.g., The Skylark"
                    : role === "STUDIO"
                    ? "e.g., EchoWorks Studio"
                    : role === "MEDIA"
                    ? "e.g., Frame & Sound Films"
                    : "e.g., Blue Note Trio"
                }
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                fullWidth
                required
              />}

              {role !== "GUEST" && <CitySelect
                name="city"
                autoComplete="address-level2"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PlaceIcon />
                    </InputAdornment>
                  ),
                }}
              />}

              <TextField
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon />
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                label="Password"
                type={showPw ? "text" : "password"}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                fullWidth
                required
                helperText="Use at least 8 characters with a mix of letters, numbers & symbols."
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPw((s) => !s)} edge="end" aria-label="toggle password visibility">
                        {showPw ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Box>
                <LinearProgress
                  variant="determinate"
                  value={(pwScore / 4) * 100}
                  sx={{ borderRadius: 999, height: 8 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Strength: {strengthLabel(pwScore)}
                </Typography>
              </Box>

              <TextField
                label="Confirm password"
                type={showConfirm ? "text" : "password"}
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                error={confirm.length > 0 && !passwordsMatch}
                helperText={
                  confirm.length > 0 && !passwordsMatch ? "Passwords do not match" : " "
                }
                fullWidth
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirm((s) => !s)} edge="end" aria-label="toggle confirm visibility">
                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <FormControlLabel
                control={<Checkbox checked={agree} onChange={(e) => setAgree(e.target.checked)} />}
                label={
                  <Typography variant="body2">
                    I agree to the{" "}
                    <MUILink component={Link} to="/terms" target="_blank" rel="noopener" underline="hover">
                      Terms of Service
                    </MUILink>{" "}
                    (version {LEGAL_DOCUMENTS.terms.version}) and{" "}
                    <MUILink component={Link} to="/privacy" target="_blank" rel="noopener" underline="hover">
                      Privacy Policy
                    </MUILink>{" "}
                    (version {LEGAL_DOCUMENTS.privacy.version}).
                  </Typography>
                }
              />

              {!!error && <Alert severity="error">{error === "SIGNUP_RETRY" ? getMessage(locale, "signupRetry") : error}</Alert>}
              {protectionError && <Alert severity="error">{getMessage(locale, "signupRetry")}</Alert>}
              {!formToken && !protectionError && <Typography role="status" variant="body2">{getMessage(locale, "signupPreparing")}</Typography>}

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!canSubmit || loading || !formToken}
                fullWidth
              >
                {loading ? "Creating account…" : "Create account"}
              </Button>
            </Stack>
          </Box>

          {/* Or */}
          <Divider flexItem />

          <Typography variant="body2" color="text.secondary" textAlign="center">
            Already have an account?{" "}
            <MUILink component={Link} to={`/auth?redirect=${encodeURIComponent(redirectTo)}`} underline="hover">
              Sign in
            </MUILink>
          </Typography>
        </Stack>
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={2200}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snack.severity} sx={{ width: "100%" }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
